import { createContext, useEffect, useState } from 'react';
import { setGlobalTheme } from '@atlaskit/tokens';

setGlobalTheme({ colorMode: 'light' });

export enum Theme {
  Light = 'light',
  Dark = 'dark',
}

type ThemeState = [Theme, React.Dispatch<React.SetStateAction<Theme>>];

function persistTheme(theme: Theme) {
  if ('localStorage' in window) {
    localStorage.setItem('theme', theme);
  }
}

export const ThemeContext = createContext([Theme.Light, () => {}] as ThemeState);

/**
 * Restores previously selected theme IF it was Dark.
 * Default theme is Light so we'll only restore the theme if the previous selection was Dark to
 * avoids `setGlobalTheme` making unnecessary dom updates
 * @param setTheme
 */
function restorePreviouslySelectedDarkTheme(setTheme: React.Dispatch<React.SetStateAction<Theme>>) {
  const previousTheme = 'localStorage' in window ? (localStorage.getItem('theme') as Theme) : null;
  if (previousTheme === Theme.Dark) {
    setTheme(previousTheme);
    setGlobalTheme({ colorMode: 'dark' });
  }
}

function useTheme() {
  const [theme, setTheme] = useState(Theme.Light);

  useEffect(() => {
    restorePreviouslySelectedDarkTheme(setTheme);
  }, []);

  const setThemeWithPersistence = (t: Theme) => {
    setTheme(t);
    if (t === Theme.Dark) {
      setGlobalTheme({ colorMode: 'dark' });
    } else {
      setGlobalTheme({ colorMode: 'light' });
    }
    persistTheme(t);
  };

  return [theme, setThemeWithPersistence] as ThemeState;
}

export default useTheme;
